import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { downloadAuctionProposal } from "../lib/auctionProposalGenerator";
import { downloadMuseumProposal } from "../lib/museumProposalGenerator";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

const stripePromise = loadStripe(
  "pk_live_51Qs7uCD0LWzRektGoLYovKcc3CvgCuCtWSw9J6WVwN5VdfIhAOW33emjshUEItKoU9pzczrKIMF3sNcXUBh2kldC00kVLN80RZ"
);

const safeJsonParse = (value, fallback) => {
  if (value == null) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const CheckoutForm = ({
  proposalDetails,
  selectedExpertId,
  userId,
  clientEmail,
  artworkId,
  artworkTitle,
  expertEmail,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const money = (value) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(Number(value || 0));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const amountHt = Number(proposalDetails?.amount_ht || 500);
  const amountTtc = useMemo(() => Math.round(amountHt * 1.2 * 100) / 100, [amountHt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    try {
      const amountTtcCents = Math.round(amountHt * 1.2 * 100);

      const response = await fetch("/api/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountTtcCents,
          proposalId: proposalDetails.proposalId,
          proposalType: proposalDetails.proposalType,
        }),
      });

      if (!response.ok) {
        throw new Error(t("proposeMuseum.payment.createError"));
      }

      const data = await response.json();

      const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent?.status === "succeeded") {
        const { error: proposalUpdateError } = await supabase
          .from("proposals")
          .update({
            status: "paid",
            payment_id: paymentIntent.id,
            payment_amount: paymentIntent.amount,
            payment_date: new Date().toISOString(),
          })
          .eq("id", proposalDetails.proposalId);

        if (proposalUpdateError) throw proposalUpdateError;

        const { error: partnerRequestError } = await supabase
          .from("partner_requests")
          .insert({
            proposal_id: proposalDetails.proposalId,
            expert_id: selectedExpertId || null,
            client_id: userId,
            artwork_id: artworkId,
            status: "pending",
          });

        if (partnerRequestError) throw partnerRequestError;

        if (expertEmail) {
          await supabase.functions.invoke("send-role-notification", {
            body: {
              to: expertEmail,
              type: "new_mission",
              artworkTitle,
              clientName: clientEmail,
            },
          });
        }

        navigate("/payment-success", {
          state: {
            proposalDetails,
            paymentId: paymentIntent.id,
          },
        });
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || t("proposeMuseum.payment.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-12 border border-gray-200 rounded-2xl p-6 bg-white">
      <div className="mb-6">
        <h2 className="text-2xl font-light mb-2">{t("proposeMuseum.payment.title")}</h2>
        <p className="text-sm text-gray-600">
          {t("proposeMuseum.payment.description")}
        </p>
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">{t("proposeMuseum.payment.amountExclTax")}</span>
          <span className="font-medium">{money(amountHt)}</span>
        </div>
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">{t("proposeMuseum.payment.vat")}</span>
          <span className="font-medium">{money(amountTtc - amountHt)}</span>
        </div>
        <div className="flex justify-between text-base pt-2 border-t border-gray-200">
          <span className="font-medium">{t("proposeMuseum.payment.totalInclTax")}</span>
          <span className="font-semibold">{money(amountTtc)}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="p-4 border border-gray-200 rounded-xl">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#000",
                  fontFamily: "system-ui, sans-serif",
                  "::placeholder": { color: "#999" },
                },
                invalid: { color: "#dc2626" },
              },
            }}
          />
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-xl border-l-4 border-red-500 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || isLoading}
          className="w-full py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isLoading ? t("proposeMuseum.payment.processing") : t("proposeMuseum.payment.pay", { amount: money(amountHt) })}
        </button>
      </form>
    </div>
  );
};

export const ProposeMuseum = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { artworkId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const money = (value) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(Number(value || 0));

  const [experts, setExperts] = useState([]);
  const [selectedExpertId, setSelectedExpertId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [artworks, setArtworks] = useState([]);
  const [proposalList, setProposalList] = useState([]);
  const [proposalDetails, setProposalDetails] = useState({});
  const [showEmailOption, setShowEmailOption] = useState(false);
  const [expert, setExpert] = useState(null);
  const [expertServices, setExpertServices] = useState([]);
  const [selectedServiceKey, setSelectedServiceKey] = useState("museum_placement");
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showExhibitionForm, setShowExhibitionForm] = useState(false);
  const [currentExhibition, setCurrentExhibition] = useState({
    title: "",
    venue: "",
    location: "",
    startDate: "",
    endDate: "",
    description: "",
  });

  const [formData, setFormData] = useState({
    artworkId: artworkId || "",
    institution: "",
    message: "",
    email: "",
    sendMethod: "service",
    proposalType: "institution_gift",
    artistBio: "",
    artworkDescription: "",
    artisticRelevance: "",
    exhibitions: [],
    estimationValue: "",
    // Champs adaptatifs (3 objectifs)
    institutionType: "museum",
    contactName: "",
    contactRole: "",
    missionFit: "",
    provenanceSummary: "",
    ownershipConfirmed: false,
    restrictions: "none",
    documentsReady: [],
    reserveValue: "",
  });

  // Objectifs métier : don/legs & prêt = institutionnel (générateur musée) ;
  // vente aux enchères = parcours distinct (générateur enchères).
  const isAuction = ["auction_sale", "auction"].includes(formData.proposalType);
  const isInstitution = !isAuction;
  const isGift = formData.proposalType === "institution_gift";

  const toggleDocument = (key) =>
    setFormData((prev) => ({
      ...prev,
      documentsReady: prev.documentsReady.includes(key)
        ? prev.documentsReady.filter((k) => k !== key)
        : [...prev.documentsReady, key],
    }));

  const selectedExpert = useMemo(
    () => experts.find((exp) => exp.id === selectedExpertId) || null,
    [experts, selectedExpertId]
  );

  const shouldShowInlinePayment = Boolean(
    proposalDetails?.proposalId &&
      proposalDetails?.amount_ht &&
      location.state?.proposalId
  );

  const fetchArtworks = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("artworks")
        .select("id, title, artist")
        .eq("client_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setArtworks(data || []);
    } catch (err) {
      console.error("Error loading artworks:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const fetchArtworkDetails = async (id) => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("artworks")
        .select("*")
        .eq("id", id)
        .single();

      if (error) return;

      const parsed = {
        ...data,
        tags: data.tags || [],
        documents: safeJsonParse(data.documents, []),
        images: safeJsonParse(data.images, []),
        provenance: safeJsonParse(data.provenance, []),
        specifications: safeJsonParse(data.specifications, {}),
        acquisitionDetails: safeJsonParse(data.acquisitionDetails, {}),
        value: data.value || 0,
      };

      setSelectedArtwork(parsed);
    } catch (err) {
      console.error("Error loading artwork details:", err);
    }
  };

  const fetchProposalList = async (id) => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("proposals")
        .select("*")
        .eq("artwork_id", id)
        .order("created_at", { ascending: false });

      if (error) return;
      setProposalList(data || []);
    } catch (err) {
      console.error("Error loading proposals:", err);
    }
  };

  const fetchExperts = async () => {
    try {
      const { data, error } = await supabase
        .from("experts")
        .select("id, name, specialty, email, contact_email, contact_phone, image_url")
        .order("name", { ascending: true });

      if (error) throw error;
      setExperts(data || []);
    } catch (err) {
      console.error("Error loading experts:", err);
    }
  };

  const fetchExpertServices = useCallback(async (expertId) => {
    if (!expertId) {
      setExpert(null);
      setExpertServices([]);
      return;
    }

    const foundExpert = experts.find((item) => item.id === expertId) || null;
    setExpert(foundExpert);

    try {
      const { data, error } = await supabase
        .from("expert_services")
        .select("*")
        .eq("expert_id", expertId)
        .order("label", { ascending: true });

      if (error) {
        setExpertServices([]);
        return;
      }

      const normalized = (data || []).filter(
        (service) =>
          service.service_key === "museum_placement" ||
          service.service_type === "museum"
      );

      setExpertServices(normalized);

      if (normalized.length > 0) {
        setSelectedServiceKey(normalized[0].service_key);
      } else {
        setSelectedServiceKey("museum_placement");
      }
    } catch (err) {
      console.error("Error loading expert services:", err);
      setExpertServices([]);
    }
  }, [experts]);

  useEffect(() => {
    if (!user?.id) return;
    fetchArtworks();
    fetchExperts();
  }, [user?.id, fetchArtworks]);

  useEffect(() => {
    if (!selectedExpertId || experts.length === 0) return;
    fetchExpertServices(selectedExpertId);
  }, [selectedExpertId, experts, fetchExpertServices]);

  useEffect(() => {
    if (artworkId) {
      setFormData((prev) => ({ ...prev, artworkId }));
      fetchProposalList(artworkId);
      fetchArtworkDetails(artworkId);
    }
  }, [artworkId]);

  useEffect(() => {
    if (!location.state) return;

    setProposalDetails({
      proposalId: location.state.proposalId || null,
      artworkTitle: location.state.artworkTitle || "",
      institution: location.state.institution || "",
      proposalType: location.state.proposalType || "museum",
      amount_ht: location.state.amount_ht || 500,
      selectedExpertId: location.state.selectedExpertId || "",
      artworkId: location.state.artworkId || artworkId || "",
      expertEmail: location.state.expertEmail || null,
    });

    if (location.state.selectedExpertId) {
      setSelectedExpertId(location.state.selectedExpertId);
    }
  }, [location.state, artworkId]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === "sendMethod") {
        setShowEmailOption(value === "self");
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "artworkId" && value) {
      fetchProposalList(value);
      fetchArtworkDetails(value);
    }
  };

  const handleExhibitionFieldChange = (e) => {
    const { name, value } = e.target;
    setCurrentExhibition((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddExhibition = () => {
    if (!currentExhibition.title || !currentExhibition.venue) {
      alert(t("proposeMuseum.alerts.exhibitionRequired"));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      exhibitions: [...prev.exhibitions, currentExhibition],
    }));

    setCurrentExhibition({
      title: "",
      venue: "",
      location: "",
      startDate: "",
      endDate: "",
      description: "",
    });
    setShowExhibitionForm(false);
  };

  const handleRemoveExhibition = (index) => {
    setFormData((prev) => ({
      ...prev,
      exhibitions: prev.exhibitions.filter((_, i) => i !== index),
    }));
  };

  const handleGenerateSelfProposal = async (selectedArtworkObj) => {
    const payload = {
      artwork: selectedArtwork || selectedArtworkObj || null,
      artworkTitle: selectedArtworkObj?.title || t("proposeMuseum.common.untitled"),
      institution: formData.institution,
      message: formData.message,
      proposalType: formData.proposalType,
      additionalData: {
        artistBio: formData.artistBio,
        artworkDescription: formData.artworkDescription,
        artisticRelevance: formData.artisticRelevance,
        exhibitions: formData.exhibitions,
        estimationValue: formData.estimationValue || selectedArtwork?.value || 0,
        reserveValue: formData.reserveValue || null,
        institutionType: formData.institutionType,
        contactName: formData.contactName,
        contactRole: formData.contactRole,
        missionFit: formData.missionFit,
        provenanceSummary: formData.provenanceSummary,
        ownershipConfirmed: formData.ownershipConfirmed,
        restrictions: isGift ? formData.restrictions : null,
        documentsReady: formData.documentsReady,
      },
    };

    if (isInstitution) {
      await downloadMuseumProposal(payload);
    } else {
      await downloadAuctionProposal(payload);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.artworkId) {
      alert(t("proposeMuseum.alerts.selectArtwork"));
      return;
    }

    if (formData.sendMethod === "service" && !selectedExpertId) {
      alert(t("proposeMuseum.alerts.selectExpert"));
      return;
    }

    try {
      // Champs communs aux 3 objectifs (provenance, propriété, checklist, expert…)
      const commonData = {
        provenanceSummary: formData.provenanceSummary,
        ownershipConfirmed: formData.ownershipConfirmed,
        documentsReady: formData.documentsReady,
        expertId: selectedExpertId || null,
        expertName: selectedExpert?.name || null,
      };

      let additionalData = {};

      if (isInstitution) {
        additionalData = {
          ...commonData,
          institutionType: formData.institutionType,
          contactName: formData.contactName,
          contactRole: formData.contactRole,
          missionFit: formData.missionFit,
          restrictions: isGift ? formData.restrictions : null,
          artistBio: formData.artistBio,
          artisticRelevance: formData.artisticRelevance,
          artworkDescription: formData.artworkDescription,
          exhibitions: formData.exhibitions,
        };
      } else {
        additionalData = {
          ...commonData,
          estimationValue: formData.estimationValue || selectedArtwork?.value || 0,
          reserveValue: formData.reserveValue || null,
          exhibitions: formData.exhibitions,
        };
      }

      const svc = expertServices.find((s) => s.service_key === selectedServiceKey);

      const line = svc
        ? {
            service_type: svc.service_type || "museum",
            description: svc.label || t("proposeMuseum.services.expertService"),
            expert_id: expert?.id ?? selectedExpertId ?? null,
            expert_name: expert?.name ?? t("proposeMuseum.services.selectedExpert"),
            qty: 1,
            unit_price_client_ht: Number(svc.client_ht) || 0,
            unit_cost_expert_ht: Number(svc.expert_ht) || 0,
            fee_model: svc.fee_model || "pass_through",
            fee_rate: svc.fee_rate ?? null,
            fee_fixed_ht: svc.fee_fixed_ht ?? 0,
          }
        : {
            service_type: "museum",
            description: t("proposeMuseum.services.defaultDescription"),
            expert_id: selectedExpertId || null,
            expert_name: selectedExpert?.name || t("proposeMuseum.services.selectedExpert"),
            qty: 1,
            unit_price_client_ht: 500,
            unit_cost_expert_ht: 500,
            fee_model: "pass_through",
            fee_rate: null,
            fee_fixed_ht: 0,
          };

      const { data, error } = await supabase
        .from("proposals")
        .insert({
          user_id: user.id,
          artwork_id: formData.artworkId,
          institution: formData.institution,
          message: formData.message,
          send_method: formData.sendMethod,
          proposal_type: formData.proposalType,
          email: formData.email || null,
          status: formData.sendMethod === "service" ? "awaiting_payment" : "pending",
          service_entries: [line],
          additional_data: additionalData,
          created_at: new Date().toISOString(),
        })
        .select();

      if (error || !data?.length) {
        throw error || new Error(t("proposeMuseum.alerts.createFailed"));
      }

      const proposalId = data[0].id;
      const selectedArtworkObj = artworks.find((a) => a.id === formData.artworkId);
      const artworkTitle = selectedArtworkObj?.title || t("proposeMuseum.common.untitled");

      if (formData.sendMethod === "service") {
        const nextProposalDetails = {
          proposalId,
          artworkTitle,
          institution: formData.institution,
          proposalType: formData.proposalType,
          amount_ht: line.unit_price_client_ht,
          selectedExpertId,
          artworkId: formData.artworkId,
          expertEmail: selectedExpert?.email || selectedExpert?.contact_email || null,
        };

        setProposalDetails(nextProposalDetails);

        navigate(`/payment/service-museum-proposal/${formData.artworkId}`, {
          state: nextProposalDetails,
        });
      } else {
        await handleGenerateSelfProposal(selectedArtworkObj);

        navigate("/proposal-confirmation", {
          state: {
            email: formData.email,
            artworkTitle,
            institution: formData.institution,
            proposalType: formData.proposalType,
          },
        });
      }
    } catch (err) {
      console.error("Error submitting proposal:", err);
      alert(t("proposeMuseum.alerts.submitError", { message: err.message || t("proposeMuseum.alerts.unknownError") }));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black mx-auto mb-4" />
          <p className="text-gray-600">{t("proposeMuseum.common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-light mb-4">{t("proposeMuseum.auth.title")}</h2>
          <p className="text-gray-600 mb-6">
            {t("proposeMuseum.auth.description")}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors"
          >
            {t("proposeMuseum.auth.login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-light tracking-tight mb-3">
            {t("proposeMuseum.title")}
          </h1>
          <p className="text-gray-600">
            {t("proposeMuseum.subtitle")}
          </p>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-medium mb-4">{t("proposeMuseum.type.title")}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {["institution_gift", "institution_loan", "auction_sale"].map((value) => {
              const active = formData.proposalType === value;
              return (
                <label key={value} className="relative cursor-pointer h-full">
                  <input
                    type="radio"
                    name="proposalType"
                    value={value}
                    checked={active}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`relative h-full p-6 border-2 rounded-xl transition-all ${active ? "border-black bg-gray-50" : "border-gray-200"}`}>
                    <div className="absolute top-4 right-4 w-5 h-5">
                      <div className={`w-5 h-5 border-2 rounded-full transition-all ${active ? "border-black" : "border-gray-300"}`}>
                        {active && <div className="absolute inset-1 bg-black rounded-full" />}
                      </div>
                    </div>
                    <div className="font-medium mb-2">{t(`proposeMuseum.type.${value}.title`)}</div>
                    <p className="text-sm text-gray-600">{t(`proposeMuseum.type.${value}.description`)}</p>
                  </div>
                </label>
              );
            })}
          </div>
          {isAuction && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              {t("proposeMuseum.type.auctionNote")}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-12">
          <div>
            <label className="block text-lg font-medium mb-4">{t("proposeMuseum.artwork.label")}</label>
            {isLoading ? (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="animate-spin h-5 w-5 border-t-2 border-b-2 border-black rounded-full" />
                <span>{t("proposeMuseum.artwork.loading")}</span>
              </div>
            ) : artworks.length === 0 ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
                {t("proposeMuseum.artwork.empty")}
              </div>
            ) : (
              <select
                name="artworkId"
                value={formData.artworkId}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors"
                required
              >
                <option value="">{t("proposeMuseum.artwork.select")}</option>
                {artworks.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.title} {a.artist ? `— ${a.artist}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-lg font-medium mb-4">
              {isAuction
                ? t("proposeMuseum.institution.auctionLabel")
                : t("proposeMuseum.institution.museumLabel")}
            </label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              placeholder={
                isAuction
                  ? t("proposeMuseum.institution.auctionPlaceholder")
                  : t("proposeMuseum.institution.museumPlaceholder")
              }
              className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors"
              required
            />
          </div>

          <div>
            <label className="block text-lg font-medium mb-4">
              {t("proposeMuseum.message.label")}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors resize-none"
              placeholder={
                isAuction
                  ? t("proposeMuseum.message.auctionPlaceholder")
                  : t("proposeMuseum.message.museumPlaceholder")
              }
              required
            />
          </div>

          {/* Provenance, propriété & checklist documentaire — communs aux 3 objectifs */}
          <div className="space-y-6">
            <div>
              <label className="block text-lg font-medium mb-4">{t("proposeMuseum.provenance.label")}</label>
              <textarea name="provenanceSummary" value={formData.provenanceSummary} onChange={handleChange} rows={3} className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors resize-none" placeholder={t("proposeMuseum.provenance.placeholder")} />
              <label className="flex items-start gap-2 mt-2 text-sm text-gray-700">
                <input type="checkbox" checked={formData.ownershipConfirmed} onChange={(e) => setFormData({ ...formData, ownershipConfirmed: e.target.checked })} className="mt-1" />
                <span>{t("proposeMuseum.provenance.ownership")}</span>
              </label>
            </div>

            <div>
              <label className="block text-lg font-medium mb-4">{t("proposeMuseum.documents.label")}</label>
              <div className="grid md:grid-cols-2 gap-2 text-sm">
                {["images", "invoice", "provenance", "condition", "authenticity", "valuation"].map((key) => (
                  <label key={key} className="flex gap-2 items-center">
                    <input type="checkbox" checked={formData.documentsReady.includes(key)} onChange={() => toggleDocument(key)} />
                    {t(`proposeMuseum.documents.items.${key}`)}
                  </label>
                ))}
              </div>
            </div>

            {isGift && (
              <div>
                <label className="block text-lg font-medium mb-4">{t("proposeMuseum.restrictions.label")}</label>
                <select name="restrictions" value={formData.restrictions} onChange={handleChange} className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors">
                  {["none", "credit_line", "display_request", "other"].map((k) => (
                    <option key={k} value={k}>{t(`proposeMuseum.restrictions.options.${k}`)}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{t("proposeMuseum.restrictions.hint")}</p>
              </div>
            )}
          </div>

          {isInstitution && (
            <div className="space-y-6">
              {/* Type d'institution + contact curatorial identifié */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t("proposeMuseum.institutionType.label")}</label>
                  <select name="institutionType" value={formData.institutionType} onChange={handleChange} className="w-full border-2 border-gray-200 rounded px-3 py-2 focus:outline-none focus:border-black transition-colors">
                    {["museum", "foundation", "frac", "university", "public", "other"].map((k) => (
                      <option key={k} value={k}>{t(`proposeMuseum.institutionType.options.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t("proposeMuseum.contact.name")}</label>
                  <input name="contactName" value={formData.contactName} onChange={handleChange} className="w-full border-2 border-gray-200 rounded px-3 py-2 focus:outline-none focus:border-black transition-colors" placeholder={t("proposeMuseum.contact.namePlaceholder")} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t("proposeMuseum.contact.role")}</label>
                  <input name="contactRole" value={formData.contactRole} onChange={handleChange} className="w-full border-2 border-gray-200 rounded px-3 py-2 focus:outline-none focus:border-black transition-colors" placeholder={t("proposeMuseum.contact.rolePlaceholder")} />
                </div>
              </div>

              {/* Adéquation avec la mission et les collections */}
              <div>
                <label className="block text-lg font-medium mb-4">{t("proposeMuseum.missionFit.label")}</label>
                <textarea name="missionFit" value={formData.missionFit} onChange={handleChange} rows={3} className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors resize-none" placeholder={t("proposeMuseum.missionFit.placeholder")} />
              </div>

              <div>
                <label className="block text-lg font-medium mb-4">{t("proposeMuseum.museum.artistBio")}</label>
                <textarea
                  name="artistBio"
                  value={formData.artistBio}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors resize-none"
                  placeholder={t("proposeMuseum.museum.artistBioPlaceholder")}
                />
              </div>

              <div>
                <label className="block text-lg font-medium mb-4">
                  {t("proposeMuseum.museum.artworkDescription")}
                </label>
                <textarea
                  name="artworkDescription"
                  value={formData.artworkDescription}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors resize-none"
                  placeholder={t("proposeMuseum.museum.artworkDescriptionPlaceholder")}
                />
              </div>

              <div>
                <label className="block text-lg font-medium mb-4">
                  {t("proposeMuseum.museum.relevance")}
                </label>
                <textarea
                  name="artisticRelevance"
                  value={formData.artisticRelevance}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors resize-none"
                  placeholder={t("proposeMuseum.museum.relevancePlaceholder")}
                />
              </div>

              <div className="border border-gray-200 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium">{t("proposeMuseum.exhibitions.title")}</h3>
                  <button
                    type="button"
                    onClick={() => setShowExhibitionForm((prev) => !prev)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    {showExhibitionForm ? t("proposeMuseum.exhibitions.close") : t("proposeMuseum.exhibitions.add")}
                  </button>
                </div>

                {formData.exhibitions.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {formData.exhibitions.map((exhibition, index) => (
                      <div
                        key={`${exhibition.title}-${index}`}
                        className="p-4 border border-gray-200 rounded-xl flex items-start justify-between gap-4"
                      >
                        <div>
                          <div className="font-medium">{exhibition.title}</div>
                          <div className="text-sm text-gray-600">
                            {exhibition.venue}
                            {exhibition.location ? `, ${exhibition.location}` : ""}
                          </div>
                          {(exhibition.startDate || exhibition.endDate) && (
                            <div className="text-xs text-gray-500 mt-1">
                              {exhibition.startDate || "?"} → {exhibition.endDate || "?"}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveExhibition(index)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          {t("proposeMuseum.exhibitions.delete")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {showExhibitionForm && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="title"
                      placeholder={t("proposeMuseum.exhibitions.fields.title")}
                      value={currentExhibition.title}
                      onChange={handleExhibitionFieldChange}
                      className="border border-gray-300 rounded px-4 py-3"
                    />
                    <input
                      type="text"
                      name="venue"
                      placeholder={t("proposeMuseum.exhibitions.fields.venue")}
                      value={currentExhibition.venue}
                      onChange={handleExhibitionFieldChange}
                      className="border border-gray-300 rounded px-4 py-3"
                    />
                    <input
                      type="text"
                      name="location"
                      placeholder={t("proposeMuseum.exhibitions.fields.location")}
                      value={currentExhibition.location}
                      onChange={handleExhibitionFieldChange}
                      className="border border-gray-300 rounded px-4 py-3"
                    />
                    <input
                      type="date"
                      name="startDate"
                      value={currentExhibition.startDate}
                      onChange={handleExhibitionFieldChange}
                      className="border border-gray-300 rounded px-4 py-3"
                    />
                    <input
                      type="date"
                      name="endDate"
                      value={currentExhibition.endDate}
                      onChange={handleExhibitionFieldChange}
                      className="border border-gray-300 rounded px-4 py-3"
                    />
                    <textarea
                      name="description"
                      placeholder={t("proposeMuseum.exhibitions.fields.description")}
                      value={currentExhibition.description}
                      onChange={handleExhibitionFieldChange}
                      rows={3}
                      className="md:col-span-2 border border-gray-300 rounded px-4 py-3 resize-none"
                    />
                    <div className="md:col-span-2">
                      <button
                        type="button"
                        onClick={handleAddExhibition}
                        className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                      >
                        {t("proposeMuseum.exhibitions.save")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {isAuction && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-lg font-medium mb-4">
                  {t("proposeMuseum.auction.estimateLabel")}
                </label>
                <input
                  type="number"
                  name="estimationValue"
                  value={formData.estimationValue}
                  onChange={handleChange}
                  placeholder={t("proposeMuseum.auction.estimatePlaceholder")}
                  className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors"
                />
              </div>
              <div>
                <label className="block text-lg font-medium mb-4">
                  {t("proposeMuseum.auction.reserveLabel")}
                </label>
                <input
                  type="number"
                  name="reserveValue"
                  value={formData.reserveValue}
                  onChange={handleChange}
                  placeholder={t("proposeMuseum.auction.reservePlaceholder")}
                  className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black transition-colors"
                />
                <p className="text-xs text-gray-500 mt-1">{t("proposeMuseum.auction.reserveHint")}</p>
              </div>
            </div>
          )}

          {isInstitution && expertServices.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
              <h3 className="text-lg font-medium mb-4">{t("proposeMuseum.services.title")}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">{t("proposeMuseum.services.service")}</label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    value={selectedServiceKey}
                    onChange={(e) => setSelectedServiceKey(e.target.value)}
                  >
                    {expertServices.map((s) => (
                      <option key={s.service_key} value={s.service_key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(() => {
                  const s = expertServices.find(
                    (x) => x.service_key === selectedServiceKey
                  );
                  if (!s) return null;

                  return (
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t("proposeMuseum.services.clientRate")}</span>
                        <span className="font-medium">{money(s.client_ht)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t("proposeMuseum.services.expertCost")}</span>
                        <span className="font-medium">{money(s.expert_ht)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {isInstitution && (
            <div>
              <label className="block text-lg font-medium mb-4">{t("proposeMuseum.expert.label")}</label>
              <select
                value={selectedExpertId}
                onChange={(e) => setSelectedExpertId(e.target.value)}
                className="w-full border-2 border-gray-200 rounded px-4 py-3 focus:outline-none focus:border-black"
                required={formData.sendMethod === "service"}
              >
                <option value="">{t("proposeMuseum.expert.select")}</option>
                {experts.map((exp) => (
                  <option key={exp.id} value={exp.id}>
                    {exp.name} — {exp.specialty}
                  </option>
                ))}
              </select>

              {expert && (
                <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm">
                  <div className="font-medium">{expert.name}</div>
                  <div className="text-gray-600">{expert.specialty}</div>
                  {(expert.contact_email || expert.email) && (
                    <div className="text-gray-500 mt-1">
                      {expert.contact_email || expert.email}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs mt-2">
                <a className="underline" href="/experts">
                  {t("proposeMuseum.expert.directory")}
                </a>
              </p>
            </div>
          )}

          <div>
            <h2 className="text-lg font-medium mb-4">{t("proposeMuseum.sendMethod.title")}</h2>
            <div className="space-y-4">
              <label className="relative cursor-pointer block">
                <input
                  type="radio"
                  name="sendMethod"
                  value="service"
                  checked={formData.sendMethod === "service"}
                  onChange={handleChange}
                  className="peer sr-only"
                />
                <div className="relative p-6 border-2 border-gray-200 rounded peer-checked:border-black peer-checked:bg-gray-50 transition-all">
                  <div className="absolute top-6 right-6 w-5 h-5 border-2 border-gray-300 rounded-full peer-checked:border-black transition-all">
                    <div
                      className={`absolute inset-1 bg-black rounded-full transition-opacity ${
                        formData.sendMethod === "service" ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </div>
                  <div className="font-medium mb-2">{t("proposeMuseum.sendMethod.professional.title")}</div>
                  <p className="text-sm text-gray-600">
                    {isAuction
                      ? t("proposeMuseum.sendMethod.professional.auctionDescription")
                      : t("proposeMuseum.sendMethod.professional.museumDescription")}
                  </p>
                </div>
              </label>

              <label className="relative cursor-pointer block">
                <input
                  type="radio"
                  name="sendMethod"
                  value="self"
                  checked={formData.sendMethod === "self"}
                  onChange={handleChange}
                  className="peer sr-only"
                />
                <div className="relative p-6 border-2 border-gray-200 rounded peer-checked:border-black peer-checked:bg-gray-50 transition-all">
                  <div className="absolute top-6 right-6 w-5 h-5 border-2 border-gray-300 rounded-full peer-checked:border-black transition-all">
                    <div
                      className={`absolute inset-1 bg-black rounded-full transition-opacity ${
                        formData.sendMethod === "self" ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </div>
                  <div className="font-medium mb-2">{t("proposeMuseum.sendMethod.self.title")}</div>
                  <p className="text-sm text-gray-600">
                    {t("proposeMuseum.sendMethod.self.description")}
                  </p>
                </div>
              </label>
            </div>

            {showEmailOption && (
              <div className="mt-6 p-4 rounded-xl" style={{ background: "#faf6ec", border: "1px solid #e6d9b3" }}>
                <label className="block text-sm font-medium mb-2">{t("proposeMuseum.sendMethod.emailLabel")}</label>
                <input
                  type="email"
                  name="email"
                  placeholder={t("proposeMuseum.sendMethod.emailPlaceholder")}
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  required={formData.sendMethod === "self"}
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors font-medium text-lg"
          >
            {formData.sendMethod === "service"
              ? t("proposeMuseum.submit.toPayment")
              : t("proposeMuseum.submit.send")}
          </button>
        </form>

        {shouldShowInlinePayment && (
          <Elements stripe={stripePromise}>
            <CheckoutForm
              proposalDetails={proposalDetails}
              selectedExpertId={proposalDetails.selectedExpertId || selectedExpertId}
              userId={user.id}
              clientEmail={user.email || formData.email || ""}
              artworkId={proposalDetails.artworkId || formData.artworkId}
              artworkTitle={proposalDetails.artworkTitle || selectedArtwork?.title || ""}
              expertEmail={proposalDetails.expertEmail || selectedExpert?.email || null}
            />
          </Elements>
        )}

        {proposalList.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h2 className="text-lg font-medium mb-6">{t("proposeMuseum.history.title")}</h2>
            <div className="space-y-4">
              {proposalList.map((proposal) => (
                <div
                  key={proposal.id}
                  className="p-4 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{proposal.institution}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {new Date(proposal.created_at).toLocaleDateString(locale)} • {t(`proposeMuseum.history.type.${proposal.proposal_type}`, { defaultValue: t("proposeMuseum.history.type.other") })}
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        proposal.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : proposal.status === "paid"
                          ? "bg-green-100 text-green-800"
                          : proposal.status === "awaiting_payment"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {proposal.status === "pending"
                        ? t("proposeMuseum.history.status.pending")
                        : proposal.status === "paid"
                        ? t("proposeMuseum.history.status.paid")
                        : proposal.status === "awaiting_payment"
                        ? t("proposeMuseum.history.status.awaitingPayment")
                        : proposal.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};